/* global theme */

/**
 * Hintaportaat quantity-tier picker + nested Lisäkustannukset fee line.
 *
 * Product €/kpl stays the list tier unit. Flat extras are added as a nested
 * cart line (parent_id) priced by Cart Transform from the parent's tier.
 */

(function () {
  function selectTier(tiers, qty) {
    if (!Array.isArray(tiers) || !Number.isFinite(qty) || qty <= 0) return null;
    const sorted = [...tiers].sort((a, b) => Number(a.min) - Number(b.min));
    if (!sorted.length || qty < Number(sorted[0].min)) return null;
    let fallback = null;
    for (const tier of sorted) {
      const min = Number(tier.min);
      const max = tier.max == null || tier.max === '' ? null : Number(tier.max);
      if (qty >= min && (max === null || qty <= max)) return tier;
      if (qty >= min) fallback = tier;
    }
    return fallback;
  }

  function tierExtra(tier) {
    const extra = Number(tier?.extra);
    return Number.isFinite(extra) && extra > 0 ? extra : 0;
  }

  /**
   * Nested fee cart line item, or null when no fee / no fee variant.
   * @param {number} parentVariantId
   * @param {{ feeVariantId?: number, tiers?: Array, quantity?: number } | HTMLElement} source
   */
  function getNestedFeeItem(parentVariantId, source) {
    const parentId = Number(parentVariantId);
    if (!Number.isFinite(parentId) || parentId <= 0) return null;

    let feeVariantId = 0;
    let tiers = [];
    let quantity = 1;

    if (source instanceof HTMLElement) {
      feeVariantId = Number(source.dataset.feeVariantId || 0);
      tiers = source.tiers || [];
      quantity = typeof source.getQuantity === 'function' ? source.getQuantity() : 1;
    } else if (source && typeof source === 'object') {
      feeVariantId = Number(source.feeVariantId || 0);
      tiers = source.tiers || [];
      quantity = Number(source.quantity) || 1;
    }

    if (!Number.isFinite(feeVariantId) || feeVariantId <= 0) return null;
    const tier = selectTier(tiers, quantity);
    if (!tier || tierExtra(tier) <= 0) return null;

    return {
      id: feeVariantId,
      quantity: 1,
      parent_id: parentId,
      properties: { _hintaportaat_fee: '1' },
    };
  }

  function cartAddSections() {
    let sections = 'cart-icon-bubble';
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
    }
    return sections;
  }

  function parseCartAddError(data, response) {
    let error = typeof data.description === 'string' ? data.description : data.message;
    if (data.errors && typeof data.errors === 'object') {
      error = Object.entries(data.errors).map((entry) => entry[1].join(', '));
    }
    if (data.status || !response.ok) {
      throw new Error(Array.isArray(error) ? error.join(', ') : (error || response.status));
    }
    return data;
  }

  function serviceGroupKey(productId, colorGroup) {
    const id = String(productId || '').trim();
    const color = String(colorGroup || '').trim().toLowerCase() || '__product__';
    return id ? `${id}::${encodeURIComponent(color)}` : '';
  }

  /** One shared lisäpalvelu set per catalog product (all Väri/Koko lines). */
  function productServiceGroupKey(productId) {
    const id = String(productId || '').trim();
    return id ? `${id}::product` : '';
  }

  function isMainProductLine(item) {
    const properties = propertiesOf(item);
    return !properties._service_group_key && !parentKeyOf(item);
  }

  function productServiceGroups(cart) {
    const groups = new Map();
    (cart?.items || []).forEach((item) => {
      if (!isMainProductLine(item)) return;
      const key = productServiceGroupKey(item.product_id);
      if (!key) return;
      const group = groups.get(key) || { total: 0, parents: [] };
      group.total += Number(item.quantity || 0);
      group.parents.push(item);
      groups.set(key, group);
    });
    return groups;
  }

  function groupTotalsForServiceKey(cart, groupKey) {
    if (String(groupKey).endsWith('::product')) {
      return productServiceGroups(cart).get(groupKey);
    }
    return parentGroups(cart).get(groupKey);
  }

  function propertiesOf(item) {
    return item && item.properties && typeof item.properties === 'object'
      ? item.properties
      : {};
  }

  function parentKeyOf(item) {
    return (
      item?.parent_relationship?.parent_key
      || item?.parent_relationship?.parent?.key
      || ''
    );
  }

  async function fetchCart() {
    const response = await fetch(`${theme.routes.cart}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(response.status);
    return response.json();
  }

  function serviceGroupState(
    cart,
    productId,
    colorGroup,
    addonVariantIds = [],
    options = {}
  ) {
    const scope = options.scope === 'product' ? 'product' : 'color';
    const groupKey = scope === 'product'
      ? productServiceGroupKey(productId)
      : serviceGroupKey(productId, colorGroup);
    const addonIds = new Set(addonVariantIds.map(Number));
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const parents = items.filter((item) => {
      const properties = propertiesOf(item);
      if (String(item.product_id) !== String(productId)) return false;
      if (properties._service_group_key || parentKeyOf(item)) return false;
      if (scope === 'color') {
        return (
          String(properties._color_group || '').trim()
          === String(colorGroup || '').trim()
        );
      }
      return true;
    });
    const parentKeys = new Set(parents.map((item) => item.key));
    const services = items.filter(
      (item) => String(propertiesOf(item)._service_group_key || '') === groupKey
    );
    const legacyServices = items.filter((item) => {
      const parentKey = parentKeyOf(item);
      return addonIds.has(Number(item.variant_id)) && parentKey && parentKeys.has(parentKey);
    });

    return {
      groupKey,
      parents,
      services,
      legacyServices,
      totalQuantity: parents.reduce((total, item) => total + Number(item.quantity || 0), 0),
      exists: services.length > 0 || legacyServices.length > 0,
    };
  }

  /**
   * Hidden properties that make a logo order recognisable on a later add, so
   * the same artwork on the same variant with the same services tops up the
   * existing line instead of starting a duplicate one.
   * @param {string} logoKey
   * @param {string} addonKey
   */
  function logoBundleProperties(logoKey, addonKey) {
    return {
      ...(logoKey ? { _logo_avain: logoKey } : {}),
      ...(addonKey ? { _lisapalvelut: addonKey } : {}),
    };
  }

  /**
   * The cart line a new logo add should top up, or null when it is a new order.
   * Color-group lines are excluded: their uploads follow the group anchor
   * instead, which has its own reuse handling.
   * @param {object} cart
   * @param {{variantId:number, logoKey:string, addonKey:string}} bundle
   */
  function findLogoBundleLine(cart, { variantId, logoKey, addonKey }) {
    if (!logoKey) return null;
    return (cart?.items || []).find((item) => {
      const properties = propertiesOf(item);
      return (
        Number(item.variant_id) === Number(variantId)
        && !parentKeyOf(item)
        && !properties._service_group_key
        && !String(properties._color_group || '').trim()
        && String(properties._logo_avain || '') === logoKey
        && String(properties._lisapalvelut || '') === String(addonKey || '')
      );
    }) || null;
  }

  /**
   * Locked tier products only accept the quantities listed on their picker, so
   * a top-up that lands between two tiers has to stay its own line.
   * @param {HTMLFormElement} form
   * @param {number} quantity
   */
  function isAllowedFormQuantity(form, quantity) {
    const input = form?.querySelector('input[name="quantity"]');
    if (input?.dataset.qtyLocked !== 'true') return true;
    try {
      const allowed = JSON.parse(input.dataset.allowedQtys || '[]').map(Number);
      return !allowed.length || allowed.includes(Number(quantity));
    } catch (error) {
      return true;
    }
  }

  /**
   * The artwork fingerprint for a product form, plus the cart line the add
   * should top up. Both add paths (with and without services) go through here.
   * @param {{form:HTMLFormElement, section:HTMLElement, variantId:number, addonKey?:string, quantity?:number}} context
   * @returns {Promise<{logoKey:string, line:object|null}>}
   */
  async function resolveLogoTopUp(context) {
    const { form, section, variantId, addonKey = '', quantity = 1 } = context;
    const customizers = [...(section || document).querySelectorAll('logo-customizer')];
    const customizer = customizers.find((element) => element.getForm?.() === form)
      || (section ? customizers[0] : null);

    let logoKey = '';
    try {
      logoKey = (await customizer?.getLogoFileKey?.()) || '';
    } catch (error) {
      console.warn('logo bundle: fingerprint failed', error);
      return { logoKey: '', line: null };
    }
    if (!logoKey) return { logoKey, line: null };

    try {
      const line = findLogoBundleLine(await fetchCart(), { variantId, logoKey, addonKey });
      const total = Number(quantity) + Number(line?.quantity || 0);
      return {
        logoKey,
        line: line && isAllowedFormQuantity(form, total) ? line : null,
      };
    } catch (error) {
      console.warn('logo bundle: cart lookup failed', error);
      return { logoKey, line: null };
    }
  }

  /**
   * JSON cart/add — fine for fee-only sync (no file properties).
   */
  async function postCartAdd(items) {
    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items,
        sections: cartAddSections(),
        sections_url: window.location.pathname,
      }),
    });
    return parseCartAddError(await response.json(), response);
  }

  async function postCartUpdate(updates, options = {}) {
    const response = await fetch(theme.routes.cartUpdate, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        updates,
        // Callers that replace a line mid-flow need the rendered cart back, so
        // the drawer and icon do not show the pre-removal state.
        ...(options.withSections
          ? { sections: cartAddSections(), sections_url: window.location.pathname }
          : {}),
      }),
    });
    return parseCartAddError(await response.json(), response);
  }

  async function postCartChange(id, quantity, properties) {
    const response = await fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id, quantity, properties }),
    });
    return parseCartAddError(await response.json(), response);
  }

  /**
   * Multipart cart/add for nested items while preserving parent line-item
   * properties — including logo-customizer file uploads.
   * JSON cannot carry File properties; product-form.js uses FormData for that.
   *
   * @param {HTMLFormElement} form
   * @param {Array<{id:number, quantity:number, parent_id?:number, parent_line_key?:string, properties?:Record<string,string>}>} items
   * @param {{logoPropertyItemIndex?:number, copyLogoProperties?:boolean}} options
   */
  async function postNestedCartAdd(form, items, options = {}) {
    const body = new FormData();
    const source = form instanceof HTMLFormElement ? new FormData(form) : null;
    const logoPropertyNames = new Set(
      form instanceof HTMLFormElement
        ? [...form.elements]
          .filter((input) => input.matches?.('[data-service-logo-property][name]'))
          .map((input) => input.name)
        : []
    );
    const logoPropertyItemIndex = Number.isInteger(options.logoPropertyItemIndex)
      ? options.logoPropertyItemIndex
      : 0;

    items.forEach((item, index) => {
      body.append(`items[${index}][id]`, String(item.id));
      body.append(`items[${index}][quantity]`, String(item.quantity));
      if (item.parent_id != null) {
        body.append(`items[${index}][parent_id]`, String(item.parent_id));
      }
      if (item.parent_line_key) {
        body.append(`items[${index}][parent_line_key]`, item.parent_line_key);
      }
      if (item.properties && typeof item.properties === 'object') {
        Object.entries(item.properties).forEach(([key, value]) => {
          if (value == null || value === '') return;
          body.append(`items[${index}][properties][${key}]`, value);
        });
      }
    });

    // Normal product properties belong to the parent. Logo properties can be
    // routed to the one service-group carrier instead of every size line.
    if (source && items.length) {
      for (const [key, value] of source.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (!match) continue;
        if (value instanceof File && !value.name) continue;
        const isLogoProperty = logoPropertyNames.has(key);
        if (isLogoProperty && options.copyLogoProperties === false) continue;
        const itemIndex = isLogoProperty ? logoPropertyItemIndex : 0;
        body.append(`items[${itemIndex}][properties][${match[1]}]`, value);
      }
      const sellingPlan = source.get('selling_plan');
      if (sellingPlan) {
        body.append('items[0][selling_plan]', String(sellingPlan));
      }
    }

    body.append('sections', cartAddSections());
    body.append('sections_url', window.location.pathname);

    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
    });
    return parseCartAddError(await response.json(), response);
  }

  /**
   * After cart qty changes, add/remove nested fee lines so they match the
   * parent's current tier. Cart Transform sets the fee amount.
   */
  async function syncCartFees() {
    if (theme._hintaportaatFeeSyncing) return;
    theme._hintaportaatFeeSyncing = true;
    try {
      const parents = document.querySelectorAll('.cart-item[data-hintaportaat-tiers]');
      const feeVariantId = Number(
        document.querySelector('[data-fee-variant-id]')?.dataset?.feeVariantId ||
          parents[0]?.dataset?.feeVariantId ||
          0
      );

      for (const parent of parents) {
        let tiers = [];
        try {
          tiers = JSON.parse(parent.dataset.hintaportaatTiers || '[]');
        } catch {
          continue;
        }
        const qty = Number(parent.querySelector('.qty-input__input')?.value || 0);
        const parentVariantId = Number(parent.dataset.variantId);
        const parentKey = parent.dataset.lineKey || '';
        const tier = selectTier(tiers, qty);
        const extra = tierExtra(tier);
        const feeChild = parentKey
          ? document.querySelector(
              `.cart-item[data-hintaportaat-fee="1"][data-parent-key="${CSS.escape(parentKey)}"]`
            )
          : null;

        if (extra > 0 && !feeChild && feeVariantId > 0 && parentVariantId > 0) {
          await postCartAdd([
            {
              id: feeVariantId,
              quantity: 1,
              parent_id: parentVariantId,
              properties: { _hintaportaat_fee: '1' },
            },
          ]);
          const cartItems = document.querySelector('cart-items');
          if (cartItems?.refresh) await cartItems.refresh();
          return;
        }

        if (extra <= 0 && feeChild) {
          const index = feeChild.id?.replace('cart-item-', '');
          if (index) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems?.updateQuantity) {
              await cartItems.updateQuantity(index, 0);
              return;
            }
          }
        }
      }
    } catch (error) {
      console.warn('hintaportaat: fee sync failed', error);
    } finally {
      theme._hintaportaatFeeSyncing = false;
    }
  }

  function parentGroups(cart) {
    const groups = new Map();
    (cart?.items || []).forEach((item) => {
      const properties = propertiesOf(item);
      if (properties._service_group_key || parentKeyOf(item)) return;
      if (!String(properties._color_group || '').trim()) return;
      const key = serviceGroupKey(item.product_id, properties._color_group);
      if (!key) return;
      const group = groups.get(key) || { total: 0, parents: [] };
      group.total += Number(item.quantity || 0);
      group.parents.push(item);
      groups.set(key, group);
    });
    return groups;
  }

  function isLogoProperty(key, value) {
    return (
      key === '_Logojen sijainnit'
      || (typeof value === 'string' && value.includes('/uploads/'))
    );
  }

  function logoPropertiesOf(item) {
    return Object.fromEntries(
      Object.entries(propertiesOf(item)).filter(([key, value]) => (
        isLogoProperty(key, value)
      ))
    );
  }

  function hasLogoProperties(item) {
    return Object.keys(logoPropertiesOf(item)).length > 0;
  }

  function withoutLogoProperties(item) {
    return Object.fromEntries(
      Object.entries(propertiesOf(item)).filter(([key, value]) => (
        !isLogoProperty(key, value)
      ))
    );
  }

  /**
   * Fixed add-ons and tier fees are one per parent line, but Shopify merges
   * identical child lines when the same parent is added again, so a second add
   * would leave the add-on at 2. Keep one child per parent at quantity one.
   * @param {Array<number>} fixedVariantIds
   */
  async function normalizeFixedNestedLines(fixedVariantIds = []) {
    const ids = new Set(fixedVariantIds.map(Number).filter(Boolean));
    if (!ids.size) return false;

    const cart = await fetchCart();
    const seen = new Map();
    const duplicates = [];
    const overshoot = [];

    (cart.items || []).forEach((item) => {
      const parentKey = parentKeyOf(item);
      if (!parentKey || !ids.has(Number(item.variant_id))) return;

      const identity = `${parentKey}::${item.variant_id}`;
      if (seen.has(identity)) {
        duplicates.push(item);
        return;
      }
      seen.set(identity, item);
      if (Number(item.quantity) !== 1) overshoot.push(item);
    });

    if (!duplicates.length && !overshoot.length) return false;

    if (duplicates.length) {
      await postCartUpdate(
        Object.fromEntries(duplicates.map((item) => [item.key, 0]))
      );
    }
    for (const item of overshoot) {
      await postCartChange(item.key, 1);
    }
    return true;
  }

  async function promoteServiceGroupAnchor(lineKey) {
    const cart = await fetchCart();
    const current = (cart.items || []).find((item) => item.key === lineKey);
    if (!current) return false;

    const key = productServiceGroupKey(current.product_id);
    const group = productServiceGroups(cart).get(key);
    if (!group) return false;

    const replacement = [...group.parents]
      .reverse()
      .find((item) => item.key !== current.key);
    if (!replacement) return false;

    const parentKeys = new Set(group.parents.map((item) => item.key));
    const services = (cart.items || []).filter((item) => {
      const properties = propertiesOf(item);
      const nestedParentKey = parentKeyOf(item);
      return (
        String(properties._service_group_key || '') === key
        || (nestedParentKey && parentKeys.has(nestedParentKey))
      );
    });
    if (services.length) {
      const servicesToRelocate = services.filter(
        (service) => parentKeyOf(service) !== current.key
      );
      if (servicesToRelocate.length) {
        await postCartUpdate(
          Object.fromEntries(servicesToRelocate.map((service) => [service.key, 0]))
        );
      }
      await postCartAdd(services.map((service) => {
        const properties = withoutLogoProperties(service);
        const remainingQuantity = Math.max(
          0,
          Number(group.total) - Number(current.quantity || 0)
        );
        return {
          id: Number(service.variant_id),
          quantity: properties._service_qty_mode === 'group' ? remainingQuantity : 1,
          parent_line_key: replacement.key,
          properties,
        };
      }));
    }

    await postCartChange(
      replacement.key,
      Number(replacement.quantity),
      {
        ...propertiesOf(replacement),
        ...logoPropertiesOf(current),
        _service_group_anchor: '1',
      }
    );
    return true;
  }

  async function replaceServiceGroupSetup(group, newAnchorVariantId) {
    if (!group) return;
    const oldServices = [...new Map(
      [...(group.services || []), ...(group.legacyServices || [])]
        .map((item) => [item.key, item])
    ).values()];
    const foldedParents = (group.parents || []).filter(
      (item) => Number(item.variant_id) === Number(newAnchorVariantId)
    );
    const removals = Object.fromEntries(
      [...oldServices, ...foldedParents].map((item) => [item.key, 0])
    );
    if (Object.keys(removals).length) await postCartUpdate(removals);

    for (const parent of (group.parents || []).filter(
      (item) => !foldedParents.some((folded) => folded.key === item.key)
    )) {
      const properties = withoutLogoProperties(parent);
      if (
        propertiesOf(parent)._service_group_anchor !== '1'
        && !hasLogoProperties(parent)
      ) {
        continue;
      }
      delete properties._service_group_anchor;
      await postCartChange(parent.key, Number(parent.quantity), properties);
    }
  }

  /**
   * A service with a show-when rule is part of a package: it may only stay in
   * the cart while the service that triggered it is still there. Returns the
   * line keys of dependents whose trigger has gone, following chains so a
   * removal cascades through nested dependencies.
   * @param {HTMLElement} cartItemsElement
   * @returns {Array<string>}
   */
  function orphanedDependentServiceKeys(cartItemsElement) {
    const parentRows = [...(cartItemsElement?.querySelectorAll(
      '.cart-item[data-service-triggers][data-line-key]'
    ) || [])];
    if (!parentRows.length) return [];

    const serviceRows = [...(cartItemsElement?.querySelectorAll(
      '.cart-item[data-lisavaruste="1"][data-line-key]'
    ) || [])];
    if (!serviceRows.length) return [];

    const orphans = new Set();

    parentRows.forEach((parentRow) => {
      let triggers;
      try {
        triggers = JSON.parse(parentRow.dataset.serviceTriggers || '{}');
      } catch (error) {
        return;
      }
      if (!triggers || !Object.keys(triggers).length) return;

      const productKey = productServiceGroupKey(parentRow.dataset.productId);
      const attached = serviceRows.filter((row) => (
        (row.dataset.parentKey && row.dataset.parentKey === parentRow.dataset.lineKey)
        || (productKey && row.dataset.serviceGroupKey === productKey)
      ));
      if (!attached.length) return;

      let cascading = true;
      while (cascading) {
        cascading = false;
        const present = new Set(
          attached
            .filter((row) => !orphans.has(row.dataset.lineKey))
            .map((row) => String(row.dataset.productId))
        );
        attached.forEach((row) => {
          if (orphans.has(row.dataset.lineKey)) return;
          const triggerId = triggers[String(row.dataset.productId)];
          if (!triggerId || present.has(String(triggerId))) return;
          orphans.add(row.dataset.lineKey);
          cascading = true;
        });
      }
    });

    return [...orphans];
  }

  async function syncServiceGroups(cartItemsElement) {
    if (theme._serviceGroupSyncing) return false;
    theme._serviceGroupSyncing = true;
    let changed = false;

    try {
      let cart = await fetchCart();
      let productGroups = productServiceGroups(cart);
      let colorGroups = parentGroups(cart);

      const migrateServiceKeys = [];
      (cart.items || []).forEach((item) => {
        const properties = propertiesOf(item);
        const key = String(properties._service_group_key || '');
        if (!key || key.endsWith('::product')) return;
        const mainProductId = properties._service_product_id;
        if (!mainProductId) return;
        const nextKey = productServiceGroupKey(mainProductId);
        if (nextKey && nextKey !== key) {
          migrateServiceKeys.push({
            key: item.key,
            quantity: Number(item.quantity),
            properties: { ...properties, _service_group_key: nextKey },
          });
        }
      });
      for (const migration of migrateServiceKeys) {
        await postCartChange(
          migration.key,
          migration.quantity,
          migration.properties
        );
        changed = true;
      }
      if (migrateServiceKeys.length) {
        cart = await fetchCart();
        productGroups = productServiceGroups(cart);
        colorGroups = parentGroups(cart);
      }

      const orphanKeys = orphanedDependentServiceKeys(cartItemsElement)
        .filter((key) => (cart.items || []).some((item) => item.key === key));
      if (orphanKeys.length) {
        await postCartUpdate(
          Object.fromEntries(orphanKeys.map((key) => [key, 0]))
        );
        changed = true;
        cart = await fetchCart();
        productGroups = productServiceGroups(cart);
        colorGroups = parentGroups(cart);
      }

      // The lisäpalvelu product metafield is authoritative. Liquid exposes its
      // effective mode on every rendered service row, allowing existing cart
      // lines to follow a merchant mode change without re-adding the product.
      const modeRows = [...(cartItemsElement?.querySelectorAll(
        '.cart-item[data-lisavaruste="1"][data-service-qty-mode][data-line-key]'
      ) || [])];
      let modeCorrected = false;
      for (const row of modeRows) {
        const item = (cart.items || []).find(
          (cartItem) => cartItem.key === row.dataset.lineKey
        );
        if (!item) continue;

        const mode = row.dataset.serviceQtyMode === 'group' ? 'group' : 'fixed';
        const groupKey = row.dataset.serviceGroupKey || '';
        const serviceGroup = groupKey
          ? groupTotalsForServiceKey(cart, groupKey)
          : null;
        const parent = row.dataset.parentKey
          ? (cart.items || []).find(
            (cartItem) => cartItem.key === row.dataset.parentKey
          )
          : null;
        const desiredQuantity = mode === 'group'
          ? Number(serviceGroup?.total || parent?.quantity || 0)
          : 1;
        if (desiredQuantity < 1) continue;

        const properties = propertiesOf(item);
        if (
          Number(item.quantity) === desiredQuantity
          && properties._service_qty_mode === mode
        ) {
          continue;
        }
        await postCartChange(
          item.key,
          desiredQuantity,
          { ...properties, _service_qty_mode: mode }
        );
        modeCorrected = true;
        changed = true;
      }
      if (modeCorrected) {
        cart = await fetchCart();
        productGroups = productServiceGroups(cart);
        colorGroups = parentGroups(cart);
      }

      const modernServices = new Map();
      (cart.items || []).forEach((item) => {
        const key = String(propertiesOf(item)._service_group_key || '');
        if (!key) return;
        const list = modernServices.get(key) || [];
        list.push(item);
        modernServices.set(key, list);
      });

      // Convert legacy per-variant nested add-ons to one product-wide service line.
      const additions = [];
      const removals = {};
      const legacySeen = new Set();
      cartItemsElement?.querySelectorAll(
        '.cart-item[data-lisavaruste="1"][data-parent-key]:not([data-service-group-key])'
      ).forEach((row) => {
        const parent = (cart.items || []).find((item) => item.key === row.dataset.parentKey);
        if (!parent) return;
        const groupKey = productServiceGroupKey(parent.product_id);
        const group = productGroups.get(groupKey);
        const variantId = Number(row.dataset.variantId);
        const identity = `${groupKey}::${variantId}`;
        const alreadyModern = (modernServices.get(groupKey) || [])
          .some((item) => Number(item.variant_id) === variantId);
        if (!legacySeen.has(identity) && !alreadyModern) {
          legacySeen.add(identity);
          const serviceMode = row.dataset.serviceQtyMode === 'group' ? 'group' : 'fixed';
          const parentProperties = propertiesOf(parent);
          additions.push({
            id: variantId,
            quantity: serviceMode === 'group' ? Number(group?.total || 1) : 1,
            properties: {
              _service_group_key: groupKey,
              _service_product_id: String(parent.product_id),
              _service_parent_title: String(parent.product_title || ''),
              _service_color: String(parentProperties._color_group || ''),
              _service_qty_mode: serviceMode,
              ...(row.dataset.requiredAddon === 'true'
                ? { _lisavaruste_required: '1' }
                : {}),
            },
          });
        }
        if (row.dataset.lineKey) removals[row.dataset.lineKey] = 0;
      });

      if (additions.length) {
        await postCartAdd(additions);
        changed = true;
      }
      if (Object.keys(removals).length) {
        await postCartUpdate(removals);
        changed = true;
      }
      if (changed) {
        cart = await fetchCart();
        productGroups = productServiceGroups(cart);
        colorGroups = parentGroups(cart);
      }

      // Fixed services stay at one. Per-unit services follow combined main qty.
      const updates = {};
      const kept = new Set();
      (cart.items || []).forEach((item) => {
        const properties = propertiesOf(item);
        const groupKey = String(properties._service_group_key || '');
        if (!groupKey) return;
        const identity = `${groupKey}::${item.variant_id}`;
        const group = groupTotalsForServiceKey(cart, groupKey);
        if (!group || group.total <= 0 || kept.has(identity)) {
          updates[item.key] = 0;
          return;
        }
        kept.add(identity);
        const desired = properties._service_qty_mode === 'group' ? group.total : 1;
        if (Number(item.quantity) !== desired) updates[item.key] = desired;
      });
      if (Object.keys(updates).length) {
        await postCartUpdate(updates);
        changed = true;
        cart = await fetchCart();
        productGroups = productServiceGroups(cart);
        colorGroups = parentGroups(cart);
      }

      const serviceGroupKeysToSync = () => {
        const keys = new Set();
        productServiceGroups(cart).forEach((_, key) => keys.add(key));
        (cart.items || []).forEach((item) => {
          const key = String(propertiesOf(item)._service_group_key || '');
          if (key) keys.add(key);
        });
        return keys;
      };

      // Keep shared uploads on exactly one main-product anchor.
      for (const groupKey of serviceGroupKeysToSync()) {
        const group = groupTotalsForServiceKey(cart, groupKey);
        if (!group?.parents?.length) continue;
        const services = (cart.items || []).filter(
          (item) => String(propertiesOf(item)._service_group_key || '') === groupKey
        );
        const serviceLogoProperties = Object.assign(
          {},
          ...services.map(logoPropertiesOf)
        );
        const markedAnchors = group.parents.filter(
          (item) => propertiesOf(item)._service_group_anchor === '1'
        );
        const logoParents = group.parents.filter(hasLogoProperties);
        const anchor =
          markedAnchors[0]
          || logoParents[0]
          || [...group.parents].reverse()[0];

        if (
          anchor
          && (
            propertiesOf(anchor)._service_group_anchor !== '1'
            || Object.keys(serviceLogoProperties).length > 0
          )
        ) {
          await postCartChange(
            anchor.key,
            Number(anchor.quantity),
            {
              ...propertiesOf(anchor),
              ...serviceLogoProperties,
              _service_group_anchor: '1',
            }
          );
          changed = true;
        }

        for (const service of services.filter(hasLogoProperties)) {
          await postCartChange(
            service.key,
            Number(service.quantity),
            withoutLogoProperties(service)
          );
          changed = true;
        }

        for (const duplicate of group.parents.filter(
          (item) => item.key !== anchor?.key && (
            hasLogoProperties(item)
            || propertiesOf(item)._service_group_anchor === '1'
          )
        )) {
          const properties = withoutLogoProperties(duplicate);
          delete properties._service_group_anchor;
          await postCartChange(
            duplicate.key,
            Number(duplicate.quantity),
            properties
          );
          changed = true;
        }
      }

      if (changed) {
        cart = await fetchCart();
        productGroups = productServiceGroups(cart);
        colorGroups = parentGroups(cart);
      }

      for (const groupKey of serviceGroupKeysToSync()) {
        const group = groupTotalsForServiceKey(cart, groupKey);
        if (!group?.parents?.length) continue;
        const anchor =
          group.parents.find(
            (item) => propertiesOf(item)._service_group_anchor === '1'
          )
          || [...group.parents].reverse()[0];
        if (!anchor) continue;

        const servicesToNest = (cart.items || []).filter((item) => (
          String(propertiesOf(item)._service_group_key || '') === groupKey
          && parentKeyOf(item) !== anchor.key
        ));
        if (!servicesToNest.length) continue;

        await postCartUpdate(
          Object.fromEntries(servicesToNest.map((service) => [service.key, 0]))
        );
        await postCartAdd(servicesToNest.map((service) => ({
          id: Number(service.variant_id),
          quantity: Number(service.quantity),
          parent_line_key: anchor.key,
          properties: withoutLogoProperties(service),
        })));
        changed = true;
      }

      if (changed && cartItemsElement?.refresh) {
        await cartItemsElement.refresh();
      }
      return changed;
    } catch (error) {
      console.warn('service groups: sync failed', error);
      return false;
    } finally {
      theme._serviceGroupSyncing = false;
    }
  }

  function getSectionVariantPrice(section) {
    const formVariantInput = section?.querySelector(
      '.js-product-form-main input[name="id"][data-variant-price]'
    );
    if (formVariantInput) {
      const fromForm = Number(formVariantInput.dataset.variantPrice);
      if (Number.isFinite(fromForm) && fromForm >= 0) return fromForm;
    }

    const picker = section?.querySelector('variant-picker');
    if (!picker) return 0;

    const dataEl = picker.querySelector('[type="application/json"]');
    if (dataEl) {
      try {
        const data = JSON.parse(dataEl.textContent);
        if (data?.price != null) return Number(data.price) || 0;
      } catch (error) {
        // fall through to picker.variant
      }
    }

    if (picker.variant?.price != null) {
      return Number(picker.variant.price) || 0;
    }
    return 0;
  }

  function computeAddonTotalCents(section) {
    const lisavaruste = section?.querySelector('lisavaruste-picker');
    if (!lisavaruste || typeof lisavaruste.getSelectedAddons !== 'function') return 0;
    const addons = lisavaruste.config?.addons;
    if (!Array.isArray(addons) || !addons.length) return 0;

    const priceByVariant = new Map(
      addons.map((addon) => [Number(addon.variantId), Number(addon.price) || 0])
    );

    return lisavaruste.getSelectedAddons().reduce((sum, selected) => {
      const unit = priceByVariant.get(Number(selected.id)) || 0;
      return sum + unit * Number(selected.quantity || 1);
    }, 0);
  }

  function computeMainTotalCents(section, qty, hintaportaatPicker) {
    if (
      hintaportaatPicker?.tiers?.length
      && typeof hintaportaatPicker.getTierForQuantity === 'function'
    ) {
      const tier = hintaportaatPicker.getTierForQuantity(qty);
      const extra = Number(tier.extra) > 0 ? Number(tier.extra) : 0;
      return Math.round((qty * tier.unitPrice + extra) * 100);
    }
    return getSectionVariantPrice(section) * qty;
  }

  function shouldShowTotalBar(hasHintaportaat, addonCents, hasLisavarustePicker) {
    if (hasHintaportaat) return true;
    return Boolean(hasLisavarustePicker) && addonCents > 0;
  }

  function getProductTotalLabel(section) {
    const hintaportaat = section?.querySelector('hintaportaat-picker');
    if (hintaportaat?.dataset?.totalLabel) return hintaportaat.dataset.totalLabel;
    return 'Hinta yhteensä:';
  }

  function getProductTotalVatLabel(section) {
    const hintaportaat = section?.querySelector('hintaportaat-picker');
    if (hintaportaat?.dataset?.vatLabel) return hintaportaat.dataset.vatLabel;
    const lisavaruste = section?.querySelector('lisavaruste-picker');
    if (lisavaruste?.config?.vatLabel) return lisavaruste.config.vatLabel;
    return '';
  }

  function createMoneyFormatter() {
    return new Intl.NumberFormat(document.documentElement.lang || 'fi-FI', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function formatMoneyFromCents(formatter, cents) {
    return formatter.format(Number(cents) / 100);
  }

  function getParentQuantity(section, hintaportaatPicker) {
    if (hintaportaatPicker && typeof hintaportaatPicker.getQuantity === 'function') {
      return hintaportaatPicker.getQuantity();
    }
    const input = section?.querySelector('.js-product-form-main input[name="quantity"]');
    const value = Number(input?.value || 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function ensureProductTotalBar(section) {
    const addToCart = section?.querySelector('.product-info__add-to-cart');
    if (!addToCart) return null;

    let bar = section.querySelector('.hintaportaat__total-bar');
    if (bar) return bar;

    const totalLabel = getProductTotalLabel(section);
    const vatLabel = getProductTotalVatLabel(section);
    bar = document.createElement('p');
    bar.className = 'hintaportaat__total-bar mb-6';
    bar.innerHTML = `
      <span class="font-bold">${totalLabel}</span>
      <span class="hintaportaat__total-value font-bold"></span>
      <span class="text-sm">${vatLabel}</span>
    `;
    addToCart.parentElement.insertBefore(bar, addToCart);
    return bar;
  }

  function updateProductTotalBar(section, qtyOverride) {
    if (!section) return;

    const hintaportaat = section.querySelector('hintaportaat-picker');
    const lisavaruste = section.querySelector('lisavaruste-picker');
    const hasHintaportaat = Boolean(hintaportaat?.tiers?.length);
    const hasLisavaruste = Boolean(lisavaruste?.config?.addons?.length);
    const qty = qtyOverride != null ? qtyOverride : getParentQuantity(section, hintaportaat);
    const mainCents = computeMainTotalCents(section, qty, hintaportaat);
    const addonCents = computeAddonTotalCents(section);
    const show = shouldShowTotalBar(hasHintaportaat, addonCents, hasLisavaruste);
    const bar = ensureProductTotalBar(section);

    if (!bar) return;

    bar.hidden = !show;
    if (!show) return;

    const totalValue = bar.querySelector('.hintaportaat__total-value');
    if (totalValue) {
      totalValue.textContent = formatMoneyFromCents(createMoneyFormatter(), mainCents + addonCents);
    }
  }

  theme.hintaportaat = {
    selectTier,
    tierExtra,
    getNestedFeeItem,
    syncCartFees,
    syncServiceGroups,
    normalizeFixedNestedLines,
    promoteServiceGroupAnchor,
    replaceServiceGroupSetup,
    postCartAdd,
    postNestedCartAdd,
    postCartUpdate,
    fetchCart,
    serviceGroupKey,
    productServiceGroupKey,
    serviceGroupState,
    logoBundleProperties,
    findLogoBundleLine,
    isAllowedFormQuantity,
    resolveLogoTopUp,
    computeAddonTotalCents,
    computeMainTotalCents,
    shouldShowTotalBar,
    updateProductTotalBar,
    ensureProductTotalBar,
    getParentQuantity,
  };
})();

class HintaportaatPicker extends HTMLElement {
  constructor() {
    super();
    this.tiers = [];
    this.sectionId = this.dataset.sectionId;
    this.minQty = Number(this.dataset.minQty) || 0;
    this.colorGrouped = this.dataset.colorGrouped === 'true';
    this.qtyLocked = this.dataset.qtyLocked === 'true';
    this.feeVariantId = Number(this.dataset.feeVariantId) || 0;
    this.vatLabel = this.dataset.vatLabel || '';
    this.totalLabel = this.dataset.totalLabel || 'Hinta yhteensä:';
    this.qtyInput = null;
    this.totalEl = null;
    this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'fi-FI', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  connectedCallback() {
    const tiersScript = this.querySelector('[data-hintaportaat-tiers]');
    if (!tiersScript) return;

    try {
      this.tiers = JSON.parse(tiersScript.textContent);
    } catch (error) {
      console.warn('hintaportaat: invalid tier data', error);
      return;
    }

    if (!this.tiers.length) return;

    this.sortTiers();
    this.bindQtyButtons();
    this.setupQuantitySync();
    this.bindVariantChange();
    this.bindAddToCart();
    this.updateUI(this.getQuantity());
  }

  sortTiers() {
    this.tiers.sort((a, b) => a.min - b.min);
  }

  allowedQuantities() {
    return this.tiers.map((t) => Number(t.min)).filter((n) => Number.isFinite(n) && n > 0);
  }

  nearestAllowedQty(qty) {
    const allowed = this.allowedQuantities();
    if (!allowed.length) return qty;
    let best = allowed[0];
    let bestDist = Math.abs(qty - best);
    for (let i = 1; i < allowed.length; i++) {
      const dist = Math.abs(qty - allowed[i]);
      if (dist < bestDist || (dist === bestDist && allowed[i] > best)) {
        best = allowed[i];
        bestDist = dist;
      }
    }
    return best;
  }

  getQuantityInput() {
    // variant-picker replaces the add-to-cart block on every variant change, so a
    // cached node goes stale and writing to it silently loses the chosen quantity.
    if (this.qtyInput?.isConnected) return this.qtyInput;
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    this.qtyInput = section?.querySelector('.js-product-form-main input[name="quantity"]');
    return this.qtyInput;
  }

  getForm() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    return section?.querySelector('.js-product-form-main');
  }

  getQuantity() {
    const input = this.getQuantityInput();
    const minQty = this.getDefaultQty();
    const value = Number(input?.value);
    if (!Number.isFinite(value) || value < minQty) return minQty;
    if (this.qtyLocked) return this.nearestAllowedQty(value);
    return value;
  }

  getDefaultQty() {
    // Color+size products enforce minimi as a color total (cart/checkout), not per line.
    if (this.colorGrouped && !this.qtyLocked) {
      return 1;
    }
    const firstTierMin = this.tiers[0]?.min || 1;
    return Math.max(this.minQty || 0, firstTierMin);
  }

  getNestedFeeItem(parentVariantId) {
    return theme.hintaportaat.getNestedFeeItem(parentVariantId, this);
  }

  clampQuantityInput() {
    const input = this.getQuantityInput();
    if (!input) return;
    const minQty = this.getDefaultQty();
    let value = Number(input.value);
    let next = value;

    if (!Number.isFinite(value) || value < minQty) {
      next = minQty;
    }

    if (this.qtyLocked) {
      next = this.nearestAllowedQty(next);
    }

    if (String(next) !== String(input.value)) {
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  setupQuantitySync() {
    const input = this.getQuantityInput();
    if (!input) return;

    const minQty = this.getDefaultQty();
    if (this.qtyLocked) {
      input.min = String(minQty);
    } else {
      input.min = '1';
    }
    if (minQty > 0) {
      input.setAttribute('data-min-qty', String(minQty));
    } else {
      input.removeAttribute('data-min-qty');
    }

    if (this.qtyLocked) {
      const allowed = this.allowedQuantities();
      input.setAttribute('data-allowed-qtys', JSON.stringify(allowed));
      input.setAttribute('data-qty-locked', 'true');
      input.step = 'any';
      this.clampQuantityInput();
    } else {
      input.removeAttribute('data-allowed-qtys');
      input.removeAttribute('data-qty-locked');
    }

    if (input.dataset.hintaportaatBound !== '1') {
      input.dataset.hintaportaatBound = '1';
      input.addEventListener('change', () => {
        if (this.qtyLocked) this.clampQuantityInput();
        this.updateUI(this.getQuantity());
      });
      input.addEventListener('blur', () => {
        if (this.qtyLocked) {
          this.clampQuantityInput();
        } else if (typeof theme.validateQuantityInput === 'function') {
          theme.validateQuantityInput(input, { showError: true });
        }
        this.updateUI(this.getQuantity());
      });
      input.addEventListener('input', () => this.updateUI(this.getQuantity()));
    }

    this.ensureTotalDisplay();
  }

  /**
   * Re-attach to the freshly rendered quantity input after a variant change.
   * Delegated on document because the picker keeps its node while the
   * add-to-cart block around the input is replaced.
   */
  bindVariantChange() {
    if (this._boundVariantChange) return;
    this._boundVariantChange = true;

    document.addEventListener('on:variant:change', (event) => {
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      if (!section || !section.contains(event.target)) return;
      this.qtyInput = null;
      this.setupQuantitySync();
      this.updateUI(this.getQuantity());
    });
  }

  /**
   * Intercept ATC when a fee line is needed and lisävaruste is not already
   * intercepting (lisävaruste appends the fee itself when it has add-ons).
   */
  bindAddToCart() {
    const form = this.getForm();
    if (!form || this._boundAtc) return;
    this._boundAtc = true;
    form.addEventListener('submit', this.handleSubmit.bind(this), true);
  }

  async handleSubmit(event) {
    const form = this.getForm();
    if (!form || event.target !== form) return;

    const formData = new FormData(form);
    const parentVariantId = Number(formData.get('id'));
    const feeItem = this.getNestedFeeItem(parentVariantId);
    if (!feeItem) return;

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const lisavaruste = section?.querySelector('lisavaruste-picker');
    if (lisavaruste && typeof lisavaruste.getSelectedAddons === 'function') {
      const addons = lisavaruste.getSelectedAddons();
      if (addons.length) return; // lisavaruste handleSubmit nests fee too
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const productForm = form.closest('product-form');
    const submitBtn = productForm?.querySelector('[name="add"]');
    const errorMsg = productForm?.querySelector('.js-form-error');
    if (typeof theme.validateQuantityInputsInForm === 'function') {
      if (!theme.validateQuantityInputsInForm(form)) return;
    } else if (typeof theme.clampQuantityInputs === 'function') {
      theme.clampQuantityInputs(form);
    }

    const parentQty = this.getQuantity();
    const items = [{ id: parentVariantId, quantity: parentQty }, feeItem];

    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
    }
    if (errorMsg) errorMsg.hidden = true;

    const cartDrawer = document.querySelector('cart-drawer');

    try {
      // Multipart so logo-customizer file properties stay on the parent line.
      const data = await theme.hintaportaat.postNestedCartAdd(form, items);
      const normalized = await theme.hintaportaat.normalizeFixedNestedLines([feeItem.id]);

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }

      if (theme.settings.afterAtc === 'page') {
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      } else if (cartDrawer) {
        setTimeout(() => {
          if (normalized) {
            cartDrawer.refresh(theme.settings.afterAtc === 'drawer');
          } else {
            cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
          }
        }, 700);
      }

      productForm?.dispatchEvent(
        new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: parentVariantId },
        })
      );
    } catch (error) {
      console.log(error); // eslint-disable-line
      if (errorMsg) {
        errorMsg.hidden = false;
        errorMsg.textContent = error?.message || String(error);
      }
      productForm?.dispatchEvent(
        new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: errorMsg?.textContent },
        })
      );
      if (cartDrawer) cartDrawer.refresh(true);
    } finally {
      if (submitBtn) {
        submitBtn.classList.add('is-success');
        submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          submitBtn.classList.remove('is-loading');
          submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }
  }

  ensureTotalDisplay() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    theme.hintaportaat?.ensureProductTotalBar?.(section);
  }

  bindQtyButtons() {
    this.querySelectorAll('.hintaportaat__qty-btn').forEach((button) => {
      button.addEventListener('click', () => {
        const qty = Number(button.dataset.qty);
        const input = this.getQuantityInput();
        if (!input || !qty) return;

        input.value = String(qty);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateUI(qty);
      });
    });
  }

  getTierForQuantity(qty) {
    let active = this.tiers[0];
    for (const tier of this.tiers) {
      if (qty >= tier.min) active = tier;
      if (tier.max != null && qty <= tier.max) return tier;
    }
    return active;
  }

  formatMoney(amount) {
    return this.moneyFormatter.format(amount);
  }

  updateUI(qty) {
    const tier = this.getTierForQuantity(qty);

    this.querySelectorAll('.hintaportaat__qty-btn').forEach((button) => {
      const isActive = Number(button.dataset.tierMin) === tier.min;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.classList.toggle('is-active', isActive);
    });

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    theme.hintaportaat?.updateProductTotalBar?.(section, qty);
  }
}

if (!customElements.get('hintaportaat-picker')) {
  customElements.define('hintaportaat-picker', HintaportaatPicker);
}
